import { useCallback, useRef } from 'react';

import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';

import cx from 'classnames';

import { useInViewportCallback } from '~/hooks/useInViewportCallback';
import { WeddingOptionFacetView } from '~/types/responseTypes';
import {
  TrackingProductLocation,
  TrackingProductType,
  trackProductClicked,
  trackProductListViewed,
} from '~/util/trackingHelper';

import { getSearchResultsURL } from '../../utils/getSearchResultsURL';
import ColorTag from '../ColorTag/ColorTag';

import styles from './ColorTagsList.module.less';

export type ColorTagsListProps = {
  colors: WeddingOptionFacetView[];
  showColorName?: boolean;
  classes?: string;
  hed?: string;
  compact?: boolean;
  className?: string;
  component?: string;
  trackingLocation?: TrackingProductLocation;
  wrap?: boolean;
};

const trackColorTagListViewed = (
  location: TrackingProductLocation,
  colors: WeddingOptionFacetView[]
) => {
  trackProductListViewed(
    location,
    colors.map((color, index) => ({ uuid: color.key, position: index + 1 })),
    BUSINESS_UNIT.REAL_WEDDINGS
  );
};

const trackColorTagClicked = (
  location: TrackingProductLocation,
  colorName: string,
  position: number
) => {
  trackProductClicked({
    location,
    productId: colorName,
    name: 'Tag',
    position,
    productType: TrackingProductType.TAG,
    businessUnit: BUSINESS_UNIT.REAL_WEDDINGS,
  });
};

export const ColorTagsList = ({
  colors,
  showColorName = false,
  classes,
  hed,
  compact,
  className,
  component,
  trackingLocation,
  wrap = false,
}: ColorTagsListProps): JSX.Element | null => {
  const colorTagsListRef = useRef(null);

  const trackColorTagListViewedCallback = useCallback(() => {
    if (trackingLocation && colors.length > 0) {
      trackColorTagListViewed(trackingLocation, colors);
    }
  }, [trackingLocation, colors]);

  useInViewportCallback({ callback: trackColorTagListViewedCallback, ref: colorTagsListRef });

  if (!colors.length) return null;

  return (
    <div
      className={cx(styles.container, compact && styles.compact, className)}
      data-testid="ColorTagsList"
      ref={colorTagsListRef}
    >
      {hed && (
        <div role="heading" aria-level={4} className={styles.hed}>
          {hed}
        </div>
      )}
      <div className={cx(styles.colorsContainer, wrap ? styles.wrapColors : '')}>
        {colors.map(({ colorSwatch, name, slug }, index) => (
          <span
            className={cx(showColorName && styles.colorContainer)}
            key={colorSwatch}
            onClick={() => {
              if (trackingLocation) {
                trackColorTagClicked(trackingLocation, name, index + 1);
              }
            }}
          >
            <div className={cx(showColorName && styles.shadow)}>
              <ColorTag
                classes={classes}
                color={colorSwatch}
                slug={slug}
                onClick={() => window.location.assign(getSearchResultsURL(slug))}
                component={component}
              />
            </div>
            {name && showColorName && <span className={styles.dek}>{name}</span>}
          </span>
        ))}
      </div>
    </div>
  );
};

export default ColorTagsList;
